import { useCallback, useMemo, useState } from 'react';
import {
  endOfDay,
  endOfISOWeek,
  setISOWeek,
  setISOWeekYear,
  startOfDay,
  startOfISOWeek,
} from 'date-fns';
import { Box, Collapse, Divider, Typography, styled, useTheme } from '@mui/material';
import DateRangePicker from '../../components/DateRangePicker';
import {
  FilterArrow,
  FilterButton,
  FilterButtonCaption,
  FilterButtonLabel,
  FilterMenu,
  SortMenuItem,
  SortMenuSection,
  SortMenuSectionHeader,
} from '../../components/FilterToolbarStyles';
import SvgIcon from '../../components/SvgIcon';
import { useIncomes } from '../../hooks/incomes';
import { PLATFORMS, PLATFORM_ICONS } from '../../constants/service';
import { formatDate } from '../../helpers/date';
import { getThemeColors } from '../../helpers/themeStyles';
import { THEME_MODES } from '../../theme';
import { ReactComponent as BonusIcon } from '../../assets/icons/32/bonus.svg';
import { ReactComponent as ReduceIcon } from '../../assets/icons/32/reduce.svg';
import { ReactComponent as ArrowDownIcon } from '../../assets/icons/16/arrow_down.svg';

const SORT_OPTIONS = {
  createdAt: {
    label: "Date d'ajout",
    options: [
      { value: 'newest', label: 'Récent → Ancien' },
      { value: 'oldest', label: 'Ancien → Récent' },
    ],
  },
  week: {
    label: 'Semaine',
    options: [
      { value: 'newest', label: 'Récente → Ancienne' },
      { value: 'oldest', label: 'Ancienne → Récente' },
    ],
  },
};

const getSortLabel = (sortConfig) =>
  SORT_OPTIONS[sortConfig.category]?.options.find((option) => option.value === sortConfig.value)
    ?.label ?? 'Récent → Ancien';

const compareIncomes = (a, b, sortConfig) => {
  const direction = sortConfig.value === 'oldest' ? 1 : -1;

  if (sortConfig.category === 'week') {
    const yearDiff = (a.year || 0) - (b.year || 0);
    if (yearDiff !== 0) return yearDiff * direction;
    const weekDiff = (a.week || 0) - (b.week || 0);
    if (weekDiff !== 0) return weekDiff * direction;
  }

  const dateA = new Date(a.createdAt || 0).getTime();
  const dateB = new Date(b.createdAt || 0).getTime();
  if (dateA !== dateB) return (dateA - dateB) * direction;

  return String(a.id || '').localeCompare(String(b.id || ''));
};

const CALCULABLE_PLATFORMS = new Set([PLATFORMS.uber, PLATFORMS.bolt]);

const CALCULATION_ROWS = [
  { key: 'taxAmount', label: 'TVA collectée' },
  { key: 'revenueExTax', label: 'CA HT' },
  { key: 'cooperativeContribution', label: 'Frais Drivio' },
  { key: 'meals', label: 'Indemnité repas' },
  { key: 'mileageAllowance', label: 'Indemnité kilométrique' },
  { key: 'otherExpenses', label: 'Autres frais' },
  { key: 'activityMargin', label: "Marge d'activité" },
  { key: 'paidLeave', label: 'Congés payés' },
  { key: 'availableActivityMargin', label: "Marge d'activité disponible" },
  { key: 'fixedGross', label: 'Fixes brut' },
  { key: 'fixedCharges', label: 'Fixes charges patronales' },
  { key: 'variableBudget', label: 'Enveloppe variable chargée' },
  { key: 'variableGross', label: 'Variable brut' },
  { key: 'variableCharges', label: 'Variable charges patronales' },
  { key: 'totalGross', label: 'Total brut' },
  { key: 'contribution', label: 'Cotisations salariales' },
  { key: 'netSalary', label: 'Net' },
  { key: 'expenseReimbursements', label: 'Remboursements' },
  { key: 'payment', label: 'Paiement', emphasize: true },
];

const formatEuro = (value) =>
  `${Number(value || 0).toLocaleString('fr-FR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} €`;

const getIncomeWeekRange = (income) => {
  const weekDate = setISOWeek(setISOWeekYear(new Date(), income.year), income.week);
  return {
    start: startOfISOWeek(weekDate),
    end: endOfISOWeek(weekDate),
  };
};

const formatWeekRange = (income) => {
  const { start, end } = getIncomeWeekRange(income);
  return `${formatDate(start, { format: 'short' })} – ${formatDate(end, { format: 'short' })}`;
};

const getSignedAmountColor = (value, fallback = '#FFFFFF') => {
  const amount = Number(value || 0);
  if (amount > 0) return '#B9FF66';
  if (amount < 0) return '#FF6B6B';
  return fallback;
};

const getIncomeSource = (income) => {
  const platform = income.service?.platform;

  if (platform && PLATFORM_ICONS[platform]) {
    return {
      key: platform,
      label:
        platform === PLATFORMS.uber
          ? 'Uber'
          : platform === PLATFORMS.bolt
            ? 'Bolt'
            : platform === PLATFORMS.heetch
              ? 'Heetch'
              : platform,
      icon: PLATFORM_ICONS[platform],
      isCalculable: CALCULABLE_PLATFORMS.has(platform),
      isPlatform: true,
    };
  }

  if (Number(income.amount) < 0) {
    return {
      key: 'REDUCTION',
      label: 'Réduction',
      icon: ReduceIcon,
      isCalculable: false,
      isReduction: true,
      isPlatform: false,
    };
  }

  return {
    key: 'BONUS',
    label: 'Bonus',
    icon: BonusIcon,
    isCalculable: false,
    isPlatform: false,
  };
};

const TabBody = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
  flex: 1,
  minHeight: 0,
  overflow: 'auto',
  padding: '0 24px 24px',
});

const Toolbar = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
  flexWrap: 'wrap',
});

const IncomeList = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
});

const IncomeCard = styled(Box)(({ theme }) => {
  const colors = getThemeColors(theme);
  const isDark = theme.palette.mode === THEME_MODES.DARK;

  return {
    borderRadius: 16,
    border: `1px solid ${colors.borderSubtle}`,
    backgroundColor: isDark ? colors.surfaceElevated : colors.surfaceSecondary,
    overflow: 'hidden',
  };
});

const IncomeHeader = styled(Box, {
  shouldForwardProp: (prop) => prop !== '$expandable',
})(({ $expandable }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 16,
  padding: '16px 20px',
  cursor: $expandable ? 'pointer' : 'default',
}));

const SourceCell = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  minWidth: 120,
  flex: '0 0 140px',
});

const MetaCell = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  flex: 1,
  minWidth: 0,
});

const AmountCell = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-end',
  gap: 2,
  marginLeft: 'auto',
});

const Chevron = styled(Box, {
  shouldForwardProp: (prop) => prop !== '$expanded',
})(({ $expanded }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 24,
  height: 24,
  flexShrink: 0,
  transform: $expanded ? 'rotate(180deg)' : 'rotate(0deg)',
  transition: 'transform 0.2s ease',
}));

const CalculationPanel = styled(Box)(({ theme }) => {
  const colors = getThemeColors(theme);

  return {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    // Match AmountCell right edge: header padding (20) + chevron (24) + gap (16)
    padding: '16px 60px 16px 20px',
    borderTop: `1px solid ${colors.borderSubtle}`,
  };
});

const CalculationRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 16,
});

const EmptyState = styled(Typography)(({ theme }) => ({
  fontSize: 14,
  color: theme.palette.text.secondary,
  padding: '32px 0',
  textAlign: 'center',
}));

const BalanceSummary = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 32,
  flexWrap: 'wrap',
});

const BalanceStat = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  minWidth: 0,
});

const formatAmount = (value) =>
  Number(value ?? 0).toLocaleString('fr-FR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const IncomeRow = ({ income }) => {
  const theme = useTheme();
  const colors = getThemeColors(theme);
  const [expanded, setExpanded] = useState(false);
  const source = getIncomeSource(income);
  const canExpand = true;
  const hasCalculation = Boolean(income.calculation);

  const metaParts = [];
  if (income.tips) metaParts.push(`Pourboires ${formatEuro(income.tips)}`);
  if (income.bonuses) metaParts.push(`Bonus ${formatEuro(income.bonuses)}`);
  if (income.cash) metaParts.push(`Espèces ${formatEuro(income.cash)}`);

  return (
    <IncomeCard>
      <IncomeHeader
        $expandable={canExpand}
        onClick={() => setExpanded((prev) => !prev)}
        role={canExpand ? 'button' : undefined}
        aria-expanded={expanded}
      >
        <SourceCell>
          {source.isPlatform ? (
            <Box
              component={source.icon}
              sx={{ width: 28, height: 28, flexShrink: 0, displayRadius: 1, overflow: 'hidden' }}
            />
          ) : (
            <SvgIcon icon={source.icon} size={28} color={colors.iconOnSurface} />
          )}
          <Typography sx={{ fontSize: 14, fontWeight: 700 }}>{source.label}</Typography>
        </SourceCell>

        <MetaCell>
          <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{formatWeekRange(income)}</Typography>
          {metaParts.length ? (
            <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
              {metaParts.join(' · ')}
            </Typography>
          ) : null}
          {income.createdAt ? (
            <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
              Ajouté le {formatDate(income.createdAt, { format: 'short', includeTime: true })}
            </Typography>
          ) : null}
        </MetaCell>

        <AmountCell>
          <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>Paiement</Typography>
          <Typography
            sx={{
              fontSize: 16,
              fontWeight: 700,
              color: getSignedAmountColor(income.amount, colors.textPrimary),
            }}
          >
            {formatEuro(income.amount)}
          </Typography>
        </AmountCell>

        <Chevron $expanded={expanded}>
          <SvgIcon icon={ArrowDownIcon} size={16} color={colors.textSecondary} />
        </Chevron>
      </IncomeHeader>

      <Collapse in={expanded}>
        {hasCalculation ? (
          <CalculationPanel>
            <CalculationRow>
              <Typography sx={{ fontSize: 13, fontWeight: 500, color: 'text.secondary' }}>
                Montant
              </Typography>
              <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                {formatEuro(income.revenue)}
              </Typography>
            </CalculationRow>
            {CALCULATION_ROWS.map((row) => (
              <CalculationRow key={row.key}>
                <Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: row.emphasize ? 700 : 500,
                    color: row.emphasize ? 'text.primary' : 'text.secondary',
                  }}
                >
                  {row.label}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: row.emphasize ? 700 : 600,
                    ...(row.key === 'payment' && {
                      color: getSignedAmountColor(income.calculation[row.key], colors.textPrimary),
                    }),
                  }}
                >
                  {formatEuro(income.calculation[row.key])}
                </Typography>
              </CalculationRow>
            ))}
          </CalculationPanel>
        ) : (
          <CalculationPanel>
            <CalculationRow>
              <Typography sx={{ fontSize: 13, fontWeight: 500, color: 'text.secondary' }}>
                Montant
              </Typography>
              <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                {formatEuro(income.revenue)}
              </Typography>
            </CalculationRow>
            <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
              {income.note
                ? income.note
                : source.isReduction
                  ? 'Montant débité du solde par un administrateur.'
                  : "Montant crédité à l'identique (pas de formule salariale Uber/Bolt)."}
            </Typography>
          </CalculationPanel>
        )}
      </Collapse>
    </IncomeCard>
  );
};

const incomeOverlapsDateRange = (income, start, end) => {
  const { start: weekStart, end: weekEnd } = getIncomeWeekRange(income);

  if (start && weekEnd < start) return false;
  if (end && weekStart > end) return false;
  return true;
};

const DriverRevenuesTab = ({ userId, balance, paidLeave }) => {
  const [dateFilterRange, setDateFilterRange] = useState({ start: null, end: null });
  const [sortAnchorEl, setSortAnchorEl] = useState(null);
  const [sortConfig, setSortConfig] = useState({ category: 'createdAt', value: 'newest' });
  const sortOpen = Boolean(sortAnchorEl);

  const { data: incomes, loading } = useIncomes({ userId });

  const handleSortOpen = (event) => setSortAnchorEl(event.currentTarget);
  const handleSortClose = () => setSortAnchorEl(null);

  const handleSortSelect = useCallback((category, value) => {
    setSortConfig({ category, value });
    setSortAnchorEl(null);
  }, []);

  const filteredIncomes = useMemo(() => {
    const { start, end } = dateFilterRange;
    const rangeStart = start ? startOfDay(start instanceof Date ? start : new Date(start)) : null;
    const rangeEnd = end ? endOfDay(end instanceof Date ? end : new Date(end)) : null;

    const next = (
      !start && !end
        ? [...incomes]
        : incomes.filter((income) => incomeOverlapsDateRange(income, rangeStart, rangeEnd))
    ).sort((a, b) => compareIncomes(a, b, sortConfig));

    return next;
  }, [incomes, dateFilterRange, sortConfig]);

  return (
    <TabBody>
      <Toolbar>
        <BalanceSummary>
          <BalanceStat>
            <Typography sx={{ fontSize: 12, fontWeight: 500, color: 'text.secondary' }}>
              Montant disponible
            </Typography>
            <Typography sx={{ fontSize: 18, fontWeight: 700 }}>
              {formatAmount(balance)} €
            </Typography>
          </BalanceStat>
          <BalanceStat>
            <Typography sx={{ fontSize: 12, fontWeight: 500, color: 'text.secondary' }}>
              Congés payés
            </Typography>
            <Typography sx={{ fontSize: 18, fontWeight: 700 }}>
              {formatAmount(paidLeave)} €
            </Typography>
          </BalanceStat>

          <DateRangePicker
            label="Date"
            placeholder="Toutes"
            value={dateFilterRange}
            onChange={setDateFilterRange}
          />
        </BalanceSummary>

        <Box>
          <FilterButton
            $open={sortOpen}
            onClick={handleSortOpen}
            aria-haspopup="true"
            aria-expanded={sortOpen ? 'true' : undefined}
          >
            <Box
              sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0 }}
            >
              <FilterButtonCaption>Trier par</FilterButtonCaption>
              <FilterButtonLabel>{getSortLabel(sortConfig)}</FilterButtonLabel>
            </Box>
            <FilterArrow open={sortOpen} />
          </FilterButton>

          <FilterMenu
            anchorEl={sortAnchorEl}
            open={sortOpen}
            onClose={(_, reason) => {
              if (reason === 'backdropClick' || reason === 'escapeKeyDown') handleSortClose();
            }}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <SortMenuSection>
              <SortMenuSectionHeader>{SORT_OPTIONS.createdAt.label}</SortMenuSectionHeader>
              {SORT_OPTIONS.createdAt.options.map(({ value, label }) => (
                <SortMenuItem
                  key={`createdAt-${value}`}
                  $selected={sortConfig.category === 'createdAt' && sortConfig.value === value}
                  onClick={() => handleSortSelect('createdAt', value)}
                  disableRipple
                >
                  {label}
                </SortMenuItem>
              ))}
            </SortMenuSection>
            <Divider sx={{ borderColor: 'divider' }} />
            <SortMenuSection>
              <SortMenuSectionHeader>{SORT_OPTIONS.week.label}</SortMenuSectionHeader>
              {SORT_OPTIONS.week.options.map(({ value, label }) => (
                <SortMenuItem
                  key={`week-${value}`}
                  $selected={sortConfig.category === 'week' && sortConfig.value === value}
                  onClick={() => handleSortSelect('week', value)}
                  disableRipple
                >
                  {label}
                </SortMenuItem>
              ))}
            </SortMenuSection>
          </FilterMenu>
        </Box>
      </Toolbar>

      {loading ? (
        <EmptyState>Chargement…</EmptyState>
      ) : filteredIncomes.length === 0 ? (
        <EmptyState>
          {dateFilterRange.start || dateFilterRange.end
            ? 'Aucun revenu pour cette période.'
            : 'Aucun revenu.'}
        </EmptyState>
      ) : (
        <IncomeList>
          {filteredIncomes.map((income) => (
            <IncomeRow key={income.id} income={income} />
          ))}
        </IncomeList>
      )}
    </TabBody>
  );
};

export default DriverRevenuesTab;
